import { EventPoster } from '@/features/events/shared/EventPoster';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/async-state';
import { Icon } from '@/components/ui/icon';
import {
  GlassPill,
  IconAction,
  SceneBackground,
  SectionHeader,
  SurfaceCard,
} from '@/components/ui/scene';
import { Text } from '@/components/ui/text';
import { UnavailableState } from '@/components/ui/UnavailableState';
import { UsernameBadge } from '@/components/ui/UsernameBadge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ReportSheet } from '@/features/reports/ReportSheet';
import { useReport } from '@/features/reports/useReport';
import { useAuth } from '@/lib/auth/auth';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import { showGuestWritePrompt } from '@/features/market/guestWrite';
import {
  createLoadTimer,
  trackEmptyStateSeen,
  trackUserFollow,
  trackUserUnfollow,
  trackVenueFollow,
  trackVenueOwnerProfileViewed,
  trackVenueShare,
  trackVenueUnfollow,
} from '@/lib/analytics/posthog';
import { handleReadOnlyMarketError } from '@/features/market/readOnlyMarket';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { getEventTagLabel } from '@/features/events/eventTags';
import { usePublicProfile } from '@/features/profile/usePublicProfile';
import { usePublicProfileMutations } from '@/features/profile/usePublicProfileMutations';
import { formatEventDate } from '@/features/events/eventFormatting';
import { getProfileInitials } from '@/features/profile/profileFormatting';
import { buildPublicIdentityLink, getEventHref, getVenueHref } from '@/lib/navigation/routes';
import { shareLink } from '@/lib/platform/share';
import { useIsDesktop } from '@/hooks/useBreakpoint';
import { Link, Stack, router, useLocalSearchParams } from 'expo-router';
import {
  ArrowLeftIcon,
  BadgeCheckIcon,
  LockIcon,
  MoreHorizontalIcon,
  ShareIcon,
  SparklesIcon,
  UserPlus2Icon,
} from 'lucide-react-native';
import * as React from 'react';
import { FlatList, Platform, Pressable, ScrollView, View, type ListRenderItem } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

const RADIOUS_ICON = require('../../assets/images/icon.png');

type ProfileSection = 'plans' | 'hosted' | 'past';

function getDefaultProfileSection({
  hostedCount,
  pastCount,
  planCount,
}: {
  hostedCount: number;
  pastCount: number;
  planCount: number;
}): ProfileSection {
  if (planCount > 0 && hostedCount === 0) {
    return 'plans';
  }
  if (hostedCount > 0) {
    return 'hosted';
  }
  if (planCount > 0) {
    return 'plans';
  }
  if (pastCount > 0) {
    return 'past';
  }
  return 'plans';
}

function ProfileSectionTabs({
  activeSection,
  hostedCount,
  onChange,
  pastCount,
  planCount,
}: {
  activeSection: ProfileSection;
  hostedCount: number;
  onChange: (section: ProfileSection) => void;
  pastCount: number;
  planCount: number;
}) {
  const sections: { key: ProfileSection; label: string; count: number }[] = [
    { key: 'plans', label: 'Plans', count: planCount },
    { key: 'hosted', label: 'Hosted', count: hostedCount },
    { key: 'past', label: 'Past', count: pastCount },
  ];

  return (
    <View className="flex-row rounded-full border border-border-strong bg-surface-raised p-1">
      {sections.map((section) => {
        const active = section.key === activeSection;
        return (
          <Pressable
            key={section.key}
            accessibilityRole="tab"
            accessibilityState={active ? { selected: true } : undefined}
            className="min-h-10 flex-1 flex-row items-center justify-center gap-2 rounded-full px-3"
            style={{
              backgroundColor: active ? DESIGN_TOKENS.colors.accent : 'transparent',
            }}
            onPress={() => onChange(section.key)}>
            <Text
              className="text-[13px] font-semibold"
              style={{
                color: active ? '#140f0d' : DESIGN_TOKENS.colors.textMuted,
              }}>
              {section.label}
            </Text>
            <Text
              className="text-[13px] font-semibold"
              style={{
                color: active ? '#140f0d' : DESIGN_TOKENS.colors.textSoft,
              }}>
              {section.count}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function ProfileSectionMetaLine({ items }: { items: string[] }) {
  return (
    <View className="flex-row flex-wrap items-center gap-2">
      {items.map((item) => (
        <View
          key={item}
          className="rounded-full border border-border bg-surface-raised px-3 py-2">
          <Text className="text-[12px] font-semibold text-muted-foreground">{item}</Text>
        </View>
      ))}
    </View>
  );
}

function VerifiedVenueBadge() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Pressable className="rounded-full p-1">
          <Icon as={BadgeCheckIcon} size={20} className="text-primary" />
        </Pressable>
      </TooltipTrigger>
      <TooltipContent side="top">
        <Text className="text-xs font-medium text-primary-foreground">Verified venue</Text>
      </TooltipContent>
    </Tooltip>
  );
}

function VenueProfileShell({
  venueName,
  avatarURL,
  username,
  isStaff,
  onOpenVenuePage,
}: {
  venueName: string;
  avatarURL?: string;
  username: string;
  isStaff: boolean;
  onOpenVenuePage: () => void;
}) {
  return (
    <SurfaceCard style={{ padding: 24 }}>
      <View style={{ gap: 16 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <Avatar
            alt={venueName}
            style={{
              width: 56,
              height: 56,
              borderRadius: 28,
              borderWidth: 1,
              borderColor: DESIGN_TOKENS.colors.borderStrong,
              backgroundColor: DESIGN_TOKENS.colors.surfaceAccent,
            }}>
            {avatarURL ? (
              <AvatarImage source={{ uri: avatarURL }} style={{ width: 56, height: 56 }} />
            ) : null}
            <AvatarFallback className="bg-transparent">
              <Text className="text-lg font-extrabold text-foreground">
                {getProfileInitials(venueName)}
              </Text>
            </AvatarFallback>
          </Avatar>

          <View style={{ flex: 1, gap: 6 }}>
            <Text
              style={{
                fontSize: 22,
                fontFamily: 'Outfit_700Bold',
                color: DESIGN_TOKENS.colors.text,
              }}>
              {venueName}
            </Text>
            <UsernameBadge
              username={username}
              isStaff={isStaff}
              textClassName="text-muted-foreground"
            />
          </View>
        </View>

        <View>
          <Text variant="default" className="leading-6 text-muted-foreground">
            This venue is best viewed on its venue page.
          </Text>
        </View>

        <View style={{ gap: 12 }}>
          <Pressable
            className="h-12 flex-row items-center justify-center rounded-[18px] bg-[#ef6a3d]"
            onPress={onOpenVenuePage}>
            <Text className="text-sm font-semibold text-primary-foreground">View Venue Page</Text>
          </Pressable>
        </View>
      </View>
    </SurfaceCard>
  );
}

export default function PublicProfileScreen() {
  const params = useLocalSearchParams<{ username: string }>();
  const session = useAuth((state) => state.session);
  const isDesktop = useIsDesktop();
  const username = params.username ?? '';
  const profileQuery = usePublicProfile(username);
  const { toggleFollow: toggleFollowMutation, toggleBlock: toggleBlockMutation } =
    usePublicProfileMutations();
  const {
    bottomSheetRef: reportSheetRef,
    target: reportTarget,
    openReportSheet,
    handleDismiss: handleReportDismiss,
    submitReport,
  } = useReport();
  const [isFollowBusy, setIsFollowBusy] = React.useState(false);
  const [activeProfileSection, setActiveProfileSection] =
    React.useState<ProfileSection>('plans');
  const profileResult = profileQuery.data ?? null;
  const isLoading = profileQuery.isPending;
  const error = profileQuery.isError
    ? getAPIErrorRecoveryCopy(profileQuery.error, 'profile', 'Unable to load this profile.')
    : null;
  const isFollowPending = isFollowBusy || toggleFollowMutation.isPending;
  const userCounty = session?.me.profile.last_county_name ?? null;
  const loadTimerRef = React.useRef(createLoadTimer('profile'));
  const viewedVenueProfileRef = React.useRef<string | null>(null);

  const isSelf = profileResult?.relationship.is_self ?? session?.me.profile.username === username;
  const isFollowing = profileResult?.relationship.is_following ?? false;
  const followsYou = profileResult?.relationship.follows_you ?? false;
  const isBlockedByMe = profileResult?.relationship.is_blocked_by_me ?? false;
  const hostedEvents = React.useMemo(
    () => profileResult?.hosted_events ?? [],
    [profileResult?.hosted_events]
  );
  const pastHostedEvents = React.useMemo(
    () => profileResult?.past_hosted_events ?? [],
    [profileResult?.past_hosted_events]
  );
  const publicRSVPs = React.useMemo(
    () => profileResult?.public_rsvps ?? [],
    [profileResult?.public_rsvps]
  );
  const isCuratorProfile =
    profileResult?.profile.username === 'radious' &&
    profileResult?.profile.display_name === 'Radious SA';
  const isVenueProfile = Boolean(profileResult?.profile.is_venue) && !isCuratorProfile;
  const profileAvatarURL = profileResult?.profile.avatar_url?.trim() ?? '';
  const shouldShowVenueShell = Boolean(
    profileResult?.profile.is_venue &&
    profileResult?.profile.venue_id &&
    !isSelf &&
    !isCuratorProfile
  );
  const followLabel = 'Follow';
  const profileStatusLabel = isSelf
    ? 'This is you'
    : profileResult?.visibility === 'limited'
      ? 'Friends only'
      : isCuratorProfile
        ? 'Curator profile'
        : followsYou
          ? 'Follows you'
          : null;
  const emptyBioLabel =
    profileResult?.visibility === 'limited'
      ? 'Only mutual follows can see this profile.'
      : isCuratorProfile
        ? 'Official SA events curated by the Radious team.'
        : 'This host has not added a bio yet.';
  const sceneTags = React.useMemo(() => {
    const tags = Array.from(
      new Set([...hostedEvents, ...publicRSVPs].flatMap((event) => event.tags))
    ).slice(0, 4);
    return tags;
  }, [hostedEvents, publicRSVPs]);
  const crowdPulled = hostedEvents.reduce((total, event) => total + event.rsvp_count, 0);
  const defaultProfileSection = React.useMemo(
    () =>
      getDefaultProfileSection({
        hostedCount: hostedEvents.length,
        pastCount: pastHostedEvents.length,
        planCount: publicRSVPs.length,
      }),
    [hostedEvents.length, pastHostedEvents.length, publicRSVPs.length]
  );
  const activeSectionTitle =
    activeProfileSection === 'plans'
      ? 'Public plans.'
      : activeProfileSection === 'hosted'
        ? isSelf
          ? 'Upcoming events.'
          : 'Recent drops.'
        : 'Past nights.';
  const activeSectionBody =
    activeProfileSection === 'plans'
      ? isSelf
        ? 'Events you are sharing publicly.'
        : `Events ${profileResult?.profile.display_name ?? 'this person'} is sharing publicly.`
      : activeProfileSection === 'hosted'
        ? isSelf
          ? 'Events you are hosting next.'
          : `Events hosted by ${profileResult?.profile.display_name ?? 'this person'}.`
        : `Ended events from ${profileResult?.profile.display_name ?? 'this profile'}.`;
  const activeSectionMeta =
    activeProfileSection === 'plans'
      ? [
          `${publicRSVPs.length} public ${publicRSVPs.length === 1 ? 'plan' : 'plans'}`,
          `${sceneTags.length} ${sceneTags.length === 1 ? 'scene' : 'scenes'}`,
        ]
      : activeProfileSection === 'hosted'
        ? [
            `${hostedEvents.length} active ${hostedEvents.length === 1 ? 'event' : 'events'}`,
            `${crowdPulled} ${crowdPulled === 1 ? 'person' : 'people'} going`,
          ]
        : [
            `${pastHostedEvents.length} past ${pastHostedEvents.length === 1 ? 'night' : 'nights'}`,
          ];
  const renderHostedEventCard = React.useCallback<ListRenderItem<(typeof hostedEvents)[number]>>(
    ({ item: event }) => (
      <Link href={getEventHref(event.id, event.title)} asChild>
        <Pressable className="w-[162px]">
          <EventPoster event={event} className="h-[208px] rounded-[24px]" />
          <Text
            className="mt-3 text-[14px] font-semibold leading-5"
            numberOfLines={2}>
            {event.title}
          </Text>
          <Text className="mt-1 text-[11px] uppercase tracking-[1.5px] text-muted-foreground">
            {event.venue_name}
          </Text>
        </Pressable>
      </Link>
    ),
    []
  );
  const renderPastHostedEventCard = React.useCallback<
    ListRenderItem<(typeof pastHostedEvents)[number]>
  >(
    ({ item: event }) => (
      <Link href={getEventHref(event.id, event.title)} asChild>
        <Pressable className="w-[162px]">
          <EventPoster event={event} className="h-[208px] rounded-[24px]" />
          <Text
            className="mt-3 text-[14px] font-semibold leading-5"
            numberOfLines={2}>
            {event.title}
          </Text>
          <Text className="mt-1 text-[11px] uppercase tracking-[1.5px] text-muted-foreground">
            Ended {formatEventDate(event.ends_at || event.starts_at)}
          </Text>
        </Pressable>
      </Link>
    ),
    []
  );
  const renderPublicPlanCard = React.useCallback<ListRenderItem<(typeof publicRSVPs)[number]>>(
    ({ item: event }) => (
      <Link href={getEventHref(event.id, event.title)} asChild>
        <Pressable className="w-[162px]">
          <EventPoster event={event} className="h-[208px] rounded-[24px]" />
          <Text
            className="mt-3 text-[14px] font-semibold leading-5"
            numberOfLines={2}>
            {event.title}
          </Text>
          <Text className="mt-1 text-[12px] uppercase tracking-[1.5px] text-muted-foreground">
            {formatEventDate(event.starts_at)}
          </Text>
        </Pressable>
      </Link>
    ),
    []
  );

  React.useEffect(() => {
    loadTimerRef.current = createLoadTimer('profile');
  }, [username]);

  React.useEffect(() => {
    setActiveProfileSection(defaultProfileSection);
  }, [defaultProfileSection, profileResult?.profile.user_id]);

  React.useEffect(() => {
    if (isLoading) {
      return;
    }

    loadTimerRef.current.done();

    if (!profileResult) {
      trackEmptyStateSeen({
        screen: 'profile',
        userCounty,
        reason: 'error',
      });
    }
  }, [isLoading, profileResult, userCounty]);

  React.useEffect(() => {
    const profileKey = profileResult?.profile.user_id ?? null;
    if (
      !isVenueProfile ||
      !profileResult ||
      !profileKey ||
      viewedVenueProfileRef.current === profileKey
    ) {
      return;
    }

    viewedVenueProfileRef.current = profileKey;
    trackVenueOwnerProfileViewed({
      venueID: profileResult.profile.venue_id,
      venueName: profileResult.profile.venue_name || profileResult.profile.display_name,
      profileUserID: profileResult.profile.user_id,
      profileUsername: profileResult.profile.username,
      userCounty,
    });
  }, [isVenueProfile, profileResult, userCounty]);

  const publicIdentityLink = React.useMemo(
    () => (profileResult ? buildPublicIdentityLink(profileResult.profile) : ''),
    [profileResult]
  );
  const handleShareProfile = React.useCallback(() => {
    if (!publicIdentityLink) {
      return;
    }
    shareLink({
      message: publicIdentityLink,
      url: publicIdentityLink,
    })
      .then((result) => {
        if (result === 'failed' || !isVenueProfile || !profileResult) {
          return;
        }

        trackVenueShare(
          profileResult.profile.venue_name || profileResult.profile.display_name,
          profileResult.profile.venue_id,
          {
            profileUserID: profileResult.profile.user_id,
            profileUsername: profileResult.profile.username,
          }
        );
      })
      .catch(() => undefined);
  }, [isVenueProfile, profileResult, publicIdentityLink]);

  const handleBack = React.useCallback(() => {
    if (Platform.OS === 'web') {
      const historyState =
        typeof window !== 'undefined'
          ? (window.history.state as { idx?: number } | null)
          : null;
      if (typeof historyState?.idx === 'number' && historyState.idx > 0 && router.canGoBack()) {
        router.back();
        return;
      }
      router.replace('/(tabs)/events');
      return;
    }

    if (router.canGoBack()) {
      router.back();
      return;
    }
    router.replace('/(tabs)/events');
  }, []);

  async function toggleFollow() {
    if (isFollowBusy) {
      return;
    }
    if (!session) {
      showGuestWritePrompt('follow');
      return;
    }
    if (!profileResult) {
      return;
    }

    setIsFollowBusy(true);
    try {
      await toggleFollowMutation.mutateAsync({
        username,
        isFollowing: profileResult.relationship.is_following,
      });
      if (isVenueProfile) {
        const venueName = profileResult.profile.venue_name || profileResult.profile.display_name;
        if (profileResult.relationship.is_following) {
          trackVenueUnfollow(venueName, profileResult.profile.venue_id, {
            profileUserID: profileResult.profile.user_id,
            profileUsername: profileResult.profile.username,
          });
        } else {
          trackVenueFollow(venueName, profileResult.profile.venue_id, {
            profileUserID: profileResult.profile.user_id,
            profileUsername: profileResult.profile.username,
          });
        }
      } else {
        if (profileResult.relationship.is_following) {
          trackUserUnfollow(profileResult.profile.user_id, profileResult.profile.username ?? '');
        } else {
          trackUserFollow(profileResult.profile.user_id, profileResult.profile.username ?? '');
        }
      }
    } catch (actionError) {
      handleReadOnlyMarketError(actionError);
    } finally {
      setIsFollowBusy(false);
    }
  }

  async function toggleBlock() {
    if (!profileResult) return;
    if (!session) {
      showGuestWritePrompt('block');
      return;
    }
    try {
      await toggleBlockMutation.mutateAsync({
        username,
        isBlocked: profileResult.relationship.is_blocked_by_me,
      });
    } catch (actionError) {
      handleReadOnlyMarketError(actionError);
    }
  }

  function openVenuePage() {
    if (!profileResult?.profile.venue_id) {
      return;
    }

    router.push(
      getVenueHref(profileResult.profile.venue_id, { source: 'profile' }, { name: profileResult.profile.venue_name })
    );
  }

  if (!profileResult && isLoading) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: DESIGN_TOKENS.colors.canvas }}>
        <SceneBackground />
        <LoadingState label="Loading profile..." />
      </SafeAreaView>
    );
  }

  if (!profileResult) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: DESIGN_TOKENS.colors.canvas }}>
        <SceneBackground />
        <UnavailableState
          eyebrow="Profile Link"
          icon={SparklesIcon}
          title="This profile is unavailable right now"
          body="That profile may be private, gone, or linked from an older share. Head back to the feed and keep exploring."
          detail={error ?? 'This profile could not be loaded right now.'}
          primaryActionLabel="Browse events"
          onPrimaryAction={() => router.replace('/(tabs)/events')}
          secondaryActionLabel="Open map"
          onSecondaryAction={() => router.replace('/(tabs)/map')}
        />
      </SafeAreaView>
    );
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <SafeAreaView style={{ flex: 1, backgroundColor: DESIGN_TOKENS.colors.canvas }}>
        <View style={{ flex: 1 }}>
          <SceneBackground />

          {!session && Platform.OS === 'web' ? (
            <View
              style={{
                backgroundColor: DESIGN_TOKENS.colors.accent,
                paddingVertical: 12,
                paddingHorizontal: 16,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}>
              <Text
                style={{
                  color: '#111008',
                  fontFamily: 'Outfit_700Bold',
                  fontSize: 13,
                  flexShrink: 1,
                }}
                numberOfLines={1}>
                Sign in to follow hosts and keep up with their events.
              </Text>
              <Pressable
                style={{
                  backgroundColor: '#111008',
                  borderRadius: 20,
                  paddingHorizontal: 14,
                  paddingVertical: 6,
                }}
                onPress={() => router.push('/sign-in')}>
                <Text
                  style={{
                    color: DESIGN_TOKENS.colors.accent,
                    fontFamily: 'Outfit_700Bold',
                    fontSize: 12,
                  }}>
                  Sign in
                </Text>
              </Pressable>
            </View>
          ) : null}

          <ScrollView
            contentContainerStyle={{ paddingBottom: 48 }}
            showsVerticalScrollIndicator={false}>
            {isDesktop ? (
              /* ── Desktop layout ── */
              <View
                style={{
                  maxWidth: 960,
                  width: '100%',
                  alignSelf: 'center',
                  paddingHorizontal: 32,
                  paddingTop: 20,
                }}>
                {/* Desktop nav bar — back left, actions right */}
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 24,
                  }}>
                  <IconAction icon={ArrowLeftIcon} onPress={handleBack} />
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <IconAction icon={ShareIcon} onPress={handleShareProfile} />
                    {session && !isSelf ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Pressable
                            style={{
                              width: 36,
                              height: 36,
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 18,
                              borderWidth: 1,
                              borderColor: 'rgba(255,255,255,0.1)',
                              backgroundColor: 'rgba(255,255,255,0.05)',
                            }}>
                            <Icon as={MoreHorizontalIcon} size={18} className="text-foreground" />
                          </Pressable>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent sideOffset={8} align="end" className="w-44">
                          <DropdownMenuItem onPress={() => toggleBlock().catch(() => undefined)}>
                            <Text className="text-sm">
                              {isBlockedByMe ? `Unblock @${username}` : `Block @${username}`}
                            </Text>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onPress={() =>
                              openReportSheet({
                                type: 'user',
                                id: profileResult.profile.user_id,
                                username,
                              })
                            }>
                            <Text className="text-sm">Report</Text>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </View>
                </View>

                {isLoading ? <LoadingState label="Refreshing..." /> : null}
                {error ? (
                  <ErrorState
                    title="Couldn't refresh profile."
                    body={error}
                    actionLabel="Retry"
                    onAction={() => profileQuery.refetch().catch(() => undefined)}
                  />
                ) : null}

                {/* Two-column body */}
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 28 }}>
                  {/* Left column — identity */}
                  <View style={{ width: 320 }}>
                    <SurfaceCard style={{ padding: 28 }}>
                      {/* Avatar + name */}
                      <View style={{ alignItems: 'center', gap: 14 }}>
                        <Avatar
                          alt={profileResult.profile.display_name || profileResult.profile.username}
                          style={{
                            width: 88,
                            height: 88,
                            borderRadius: 44,
                            backgroundColor: DESIGN_TOKENS.colors.accent,
                            borderWidth: 3,
                            borderColor: DESIGN_TOKENS.colors.canvas,
                          }}>
                          {isCuratorProfile ? (
                            <AvatarImage source={RADIOUS_ICON} style={{ width: 88, height: 88 }} />
                          ) : profileAvatarURL.length > 0 ? (
                            <AvatarImage
                              source={{ uri: profileAvatarURL }}
                              style={{ width: 88, height: 88 }}
                            />
                          ) : (
                            <AvatarFallback className="bg-transparent">
                              <Text
                                style={{
                                  fontSize: 28,
                                  fontFamily: 'Syne_800ExtraBold',
                                  color: '#140f0d',
                                }}>
                                {getProfileInitials(profileResult.profile.display_name)}
                              </Text>
                            </AvatarFallback>
                          )}
                        </Avatar>
                      </View>

                      {/* Name + username — centered */}
                      <View style={{ alignItems: 'center', gap: 4, marginBottom: 16 }}>
                        <View
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                            flexWrap: 'wrap',
                          }}>
                          <Text
                            style={{
                              fontSize: 20,
                              fontFamily: 'Outfit_700Bold',
                              color: DESIGN_TOKENS.colors.text,
                              textAlign: 'center',
                            }}>
                            {profileResult.profile.display_name}
                          </Text>
                          {isVenueProfile ? <VerifiedVenueBadge /> : null}
                        </View>
                        <View style={{ alignItems: 'center' }}>
                          <UsernameBadge
                            username={profileResult.profile.username}
                            isStaff={profileResult.profile.is_staff}
                            textClassName="text-muted-foreground"
                          />
                        </View>
                      </View>

                      {profileStatusLabel && (isSelf || followsYou) ? (
                        <View style={{ alignItems: 'center', marginBottom: 16 }}>
                          {isSelf ? (
                            <GlassPill label={profileStatusLabel} icon={SparklesIcon} active />
                          ) : (
                            <View
                              style={{
                                borderRadius: 999,
                                borderWidth: 1,
                                borderColor: DESIGN_TOKENS.colors.border,
                                backgroundColor: DESIGN_TOKENS.colors.canvasInset,
                                paddingHorizontal: 12,
                                paddingVertical: 6,
                              }}>
                              <Text
                                style={{
                                  fontSize: 12,
                                  fontFamily: 'Outfit_600SemiBold',
                                  color: DESIGN_TOKENS.colors.textMuted,
                                }}>
                                {profileStatusLabel}
                              </Text>
                            </View>
                          )}
                        </View>
                      ) : null}

                      {/* Bio */}
                      <Text
                        variant="default"
                        style={{ fontSize: 13, textAlign: 'center', lineHeight: 22 }}
                        className="text-muted-foreground">
                        {profileResult.profile.bio || emptyBioLabel}
                      </Text>

                      {/* Stats — 3-column with dividers */}
                      <View
                        style={{
                          flexDirection: 'row',
                          marginTop: 20,
                          paddingTop: 20,
                          borderTopWidth: 1,
                          borderTopColor: DESIGN_TOKENS.colors.border,
                        }}>
                        <View style={{ flex: 1, alignItems: 'center', gap: 3 }}>
                          <Text
                            style={{
                              fontSize: 22,
                              fontFamily: 'Outfit_700Bold',
                              color: DESIGN_TOKENS.colors.text,
                            }}>
                            {profileResult.stats.hosted_count}
                          </Text>
                          <Text
                            style={{
                              fontSize: 10,
                              fontFamily: 'Outfit_600SemiBold',
                              color: DESIGN_TOKENS.colors.textMuted,
                              textTransform: 'uppercase',
                              letterSpacing: 1.2,
                            }}>
                            Hosted
                          </Text>
                        </View>
                        <View
                          style={{
                            width: 1,
                            backgroundColor: DESIGN_TOKENS.colors.border,
                            marginVertical: 4,
                          }}
                        />
                        <View style={{ flex: 1, alignItems: 'center', gap: 3 }}>
                          <Text
                            style={{
                              fontSize: 22,
                              fontFamily: 'Outfit_700Bold',
                              color: DESIGN_TOKENS.colors.text,
                            }}>
                            {profileResult.stats.follower_count}
                          </Text>
                          <Text
                            style={{
                              fontSize: 10,
                              fontFamily: 'Outfit_600SemiBold',
                              color: DESIGN_TOKENS.colors.textMuted,
                              textTransform: 'uppercase',
                              letterSpacing: 1.2,
                            }}>
                            Followers
                          </Text>
                        </View>
                        <View
                          style={{
                            width: 1,
                            backgroundColor: DESIGN_TOKENS.colors.border,
                            marginVertical: 4,
                          }}
                        />
                        <View style={{ flex: 1, alignItems: 'center', gap: 3 }}>
                          <Text
                            style={{
                              fontSize: 22,
                              fontFamily: 'Outfit_700Bold',
                              color: DESIGN_TOKENS.colors.text,
                            }}>
                            {profileResult.stats.following_count}
                          </Text>
                          <Text
                            style={{
                              fontSize: 10,
                              fontFamily: 'Outfit_600SemiBold',
                              color: DESIGN_TOKENS.colors.textMuted,
                              textTransform: 'uppercase',
                              letterSpacing: 1.2,
                            }}>
                            Following
                          </Text>
                        </View>
                      </View>

                      {/* Actions */}
                      {!isSelf && !isVenueProfile ? (
                        <View style={{ marginTop: 20 }}>
                          <Pressable
                            style={{
                              height: 48,
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 8,
                              borderRadius: 18,
                              backgroundColor: DESIGN_TOKENS.colors.accent,
                              opacity: isFollowPending ? 0.55 : 1,
                            }}
                            disabled={isFollowPending}
                            onPress={() => toggleFollow().catch(() => undefined)}>
                            <Icon
                              as={UserPlus2Icon}
                              size={16}
                              style={{ color: '#140f0d' }}
                              {...({} as any)}
                            />
                            <Text
                              style={{
                                fontSize: 14,
                                fontFamily: 'Outfit_700Bold',
                                color: '#140f0d',
                              }}>
                              {isFollowing ? 'Following' : followLabel}
                            </Text>
                          </Pressable>
                        </View>
                      ) : null}
                      {!isSelf && isVenueProfile && profileResult.profile.venue_id ? (
                        <View style={{ marginTop: 20 }}>
                          <Pressable
                            style={{
                              height: 48,
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 18,
                              backgroundColor: DESIGN_TOKENS.colors.accent,
                            }}
                            onPress={openVenuePage}>
                            <Text
                              style={{
                                fontSize: 14,
                                fontFamily: 'Outfit_700Bold',
                                color: '#140f0d',
                              }}>
                              View Venue Page
                            </Text>
                          </Pressable>
                        </View>
                      ) : null}

                      {/* Scene tags */}
                      {sceneTags.length > 0 ? (
                        <View
                          style={{
                            flexDirection: 'row',
                            flexWrap: 'wrap',
                            gap: 8,
                            marginTop: 20,
                          }}>
                          {sceneTags.map((tag) => (
                            <View
                              key={tag}
                              style={{
                                borderRadius: 999,
                                borderWidth: 1,
                                borderColor: DESIGN_TOKENS.colors.border,
                                backgroundColor: DESIGN_TOKENS.colors.canvasInset,
                                paddingHorizontal: 12,
                                paddingVertical: 7,
                              }}>
                              <Text
                                style={{
                                  fontSize: 12,
                                  fontFamily: 'Outfit_600SemiBold',
                                  letterSpacing: 1.4,
                                  color: DESIGN_TOKENS.colors.textSoft,
                                  textTransform: 'uppercase',
                                }}>
                                {getEventTagLabel(tag)}
                              </Text>
                            </View>
                          ))}
                        </View>
                      ) : null}
                    </SurfaceCard>
                  </View>

                  {/* Right column — events */}
                  <View style={{ flex: 1 }}>
                    {shouldShowVenueShell ? (
                      <VenueProfileShell
                        venueName={
                          profileResult.profile.venue_name || profileResult.profile.display_name
                        }
                        avatarURL={profileAvatarURL.length > 0 ? profileAvatarURL : undefined}
                        username={profileResult.profile.username}
                        isStaff={profileResult.profile.is_staff}
                        onOpenVenuePage={openVenuePage}
                      />
                    ) : profileResult.visibility === 'limited' ? (
                      /* Private profile — centered full-column locked state */
                      <View
                        style={{
                          flex: 1,
                          minHeight: 380,
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 16,
                          padding: 40,
                        }}>
                        <View
                          style={{
                            width: 56,
                            height: 56,
                            borderRadius: 28,
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            borderWidth: 1,
                            borderColor: 'rgba(255,255,255,0.1)',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                          <Icon as={LockIcon} size={24} className="text-muted-foreground" />
                        </View>
                        <View style={{ alignItems: 'center', gap: 10 }}>
                          <Text
                            style={{
                              fontSize: 28,
                              fontFamily: 'Syne_800ExtraBold',
                              color: DESIGN_TOKENS.colors.text,
                              textAlign: 'center',
                            }}>
                            Private profile.
                          </Text>
                          <Text
                            style={{
                              fontSize: 15,
                              color: DESIGN_TOKENS.colors.textMuted,
                              textAlign: 'center',
                              lineHeight: 24,
                              maxWidth: 320,
                            }}>
                            Plans and hosted events stay hidden until you both follow each other.
                          </Text>
                        </View>
                      </View>
                    ) : (
                      <>
                        <View style={{ marginBottom: 20 }}>
                          <SectionHeader title={activeSectionTitle} />
                        </View>
                        <View style={{ marginBottom: 20 }}>
                          <ProfileSectionTabs
                            activeSection={activeProfileSection}
                            hostedCount={hostedEvents.length}
                            onChange={setActiveProfileSection}
                            pastCount={pastHostedEvents.length}
                            planCount={publicRSVPs.length}
                          />
                        </View>

                        {activeProfileSection === 'hosted' && hostedEvents.length ? (
                          <>
                            <View style={{ marginBottom: 16 }}>
                              <ProfileSectionMetaLine items={activeSectionMeta} />
                            </View>
                            <Text
                              variant="default"
                              className="mb-5 leading-6 text-muted-foreground">
                              {activeSectionBody}
                            </Text>
                            <View
                              style={{
                                flexDirection: 'row',
                                flexWrap: 'wrap',
                                gap: 16,
                              }}>
                              {hostedEvents.map((event) => (
                                <Link key={event.id} href={getEventHref(event.id, event.title)} asChild>
                                  <Pressable style={{ width: 'calc(50% - 8px)' as any }}>
                                    <EventPoster
                                      event={event}
                                      className="h-[220px] rounded-[20px]"
                                      showMeta={false}
                                    />
                                    <Text
                                      variant="small"
                                      style={{ marginTop: 10, fontFamily: 'Outfit_700Bold' }}
                                      numberOfLines={2}>
                                      {event.title}
                                    </Text>
                                    <Text
                                      variant="small"
                                      className="mt-1 text-muted-foreground"
                                      numberOfLines={1}>
                                      {event.venue_name}
                                    </Text>
                                  </Pressable>
                                </Link>
                              ))}
                            </View>
                          </>
                        ) : activeProfileSection === 'plans' && publicRSVPs.length ? (
                          <>
                            <View style={{ marginBottom: 16 }}>
                              <ProfileSectionMetaLine items={activeSectionMeta} />
                            </View>
                            <Text
                              variant="default"
                              className="mb-5 leading-6 text-muted-foreground">
                              {activeSectionBody}
                            </Text>
                            <View
                              style={{
                                flexDirection: 'row',
                                flexWrap: 'wrap',
                                gap: 16,
                              }}>
                              {publicRSVPs.map((event) => (
                                <Link key={event.id} href={getEventHref(event.id, event.title)} asChild>
                                  <Pressable style={{ width: 'calc(50% - 8px)' as any }}>
                                    <EventPoster
                                      event={event}
                                      className="h-[220px] rounded-[20px]"
                                      showMeta={false}
                                    />
                                    <Text
                                      variant="small"
                                      style={{ marginTop: 10, fontFamily: 'Outfit_700Bold' }}
                                      numberOfLines={2}>
                                      {event.title}
                                    </Text>
                                    <Text
                                      variant="small"
                                      className="mt-1 text-muted-foreground"
                                      numberOfLines={1}>
                                      {event.venue_name}
                                    </Text>
                                  </Pressable>
                                </Link>
                              ))}
                            </View>
                          </>
                        ) : activeProfileSection === 'past' && pastHostedEvents.length ? (
                          <View
                            style={{
                              flexDirection: 'row',
                              flexWrap: 'wrap',
                              gap: 16,
                            }}>
                            {pastHostedEvents.map((event) => (
                              <Link key={event.id} href={getEventHref(event.id, event.title)} asChild>
                                <Pressable style={{ width: 'calc(50% - 8px)' as any }}>
                                  <EventPoster
                                    event={event}
                                    className="h-[220px] rounded-[20px]"
                                    showMeta={false}
                                  />
                                  <Text
                                    variant="small"
                                    style={{ marginTop: 10, fontFamily: 'Outfit_700Bold' }}
                                    numberOfLines={2}>
                                    {event.title}
                                  </Text>
                                  <Text
                                    variant="small"
                                    className="mt-1 text-muted-foreground"
                                    numberOfLines={1}>
                                    Ended {formatEventDate(event.ends_at || event.starts_at)}
                                  </Text>
                                </Pressable>
                              </Link>
                            ))}
                          </View>
                        ) : (
                          <EmptyState
                            title="Quiet for now."
                            body="There are no active hosted events or public plans on this profile right now."
                          />
                        )}
                      </>
                    )}
                  </View>
                </View>
              </View>
            ) : (
              /* ── Mobile layout (unchanged) ── */
              <View>
                <View className="px-4 pb-2 pt-3">
                  <View className="mb-5 flex-row items-center justify-between">
                    <IconAction icon={ArrowLeftIcon} onPress={handleBack} />
                    <View className="flex-row items-center gap-2">
                      <IconAction
                        icon={ShareIcon}
                        onPress={handleShareProfile}
                      />
                      {session && !isSelf ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Pressable className="size-9 items-center justify-center rounded-full border border-white/10 bg-white/5">
                              <Icon as={MoreHorizontalIcon} size={18} className="text-foreground" />
                            </Pressable>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent sideOffset={8} align="end" className="w-44">
                            <DropdownMenuItem onPress={() => toggleBlock().catch(() => undefined)}>
                              <Text className="text-sm">
                                {isBlockedByMe ? `Unblock @${username}` : `Block @${username}`}
                              </Text>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onPress={() =>
                                openReportSheet({
                                  type: 'user',
                                  id: profileResult.profile.user_id,
                                  username,
                                })
                              }>
                              <Text className="text-sm">Report</Text>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : null}
                    </View>
                  </View>

                  {isBlockedByMe ? (
                    <View className="mx-0 mb-4 flex-row items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                      <Text className="flex-1 text-sm text-muted-foreground">
                        You&apos;ve blocked this user.
                      </Text>
                      <Pressable onPress={() => toggleBlock().catch(() => undefined)}>
                        <Text className="text-sm font-semibold">Unblock</Text>
                      </Pressable>
                    </View>
                  ) : null}

                </View>

                {isLoading ? <LoadingState label="Refreshing profile..." /> : null}

                {error ? (
                  <ErrorState
                    title="Couldn't refresh profile."
                    body={error}
                    actionLabel="Retry"
                    onAction={() => profileQuery.refetch().catch(() => undefined)}
                  />
                ) : null}

                <View className="mt-4 px-4">
                  <SurfaceCard className="bg-[#14110f]/96">
                    {/* Avatar centered */}
                    <View className="mb-4 items-center">
                      <Avatar
                        alt={profileResult.profile.display_name || profileResult.profile.username}
                        className="size-[80px] rounded-full border-2 border-white/15 bg-[#ef6a3d]">
                        {isCuratorProfile ? (
                          <AvatarImage source={RADIOUS_ICON} className="h-full w-full" />
                        ) : profileAvatarURL.length > 0 ? (
                          <AvatarImage
                            source={{ uri: profileAvatarURL }}
                            className="h-full w-full"
                          />
                        ) : (
                          <AvatarFallback className="bg-transparent">
                            <Text
                              style={{
                                fontSize: 26,
                                fontFamily: 'Syne_800ExtraBold',
                                color: '#140f0d',
                              }}>
                              {getProfileInitials(profileResult.profile.display_name)}
                            </Text>
                          </AvatarFallback>
                        )}
                      </Avatar>
                    </View>

                    {/* Name + username centered */}
                    <View className="mb-4 items-center gap-1">
                      <View className="flex-row items-center justify-center gap-2">
                        <Text
                          className="shrink text-center text-[20px] font-bold"
                          numberOfLines={2}>
                          {profileResult.profile.display_name}
                        </Text>
                        {isVenueProfile ? <VerifiedVenueBadge /> : null}
                      </View>
                      <View className="items-center">
                        <UsernameBadge
                          username={profileResult.profile.username}
                          isStaff={profileResult.profile.is_staff}
                          textClassName="text-muted-foreground"
                        />
                      </View>
                    </View>

                    {/* Status — only show for self (sparkles) or "Follows you" */}
                    {profileStatusLabel && (isSelf || followsYou) ? (
                      <View className="mb-4 items-center">
                        {isSelf ? (
                          <GlassPill label={profileStatusLabel} icon={SparklesIcon} active />
                        ) : (
                          <View className="rounded-full border border-border-strong bg-surface-raised px-3 py-1.5">
                            <Text className="text-[12px] font-semibold text-muted-foreground">
                              {profileStatusLabel}
                            </Text>
                          </View>
                        )}
                      </View>
                    ) : null}

                    {/* Bio */}
                    <Text className="mb-5 text-center text-[14px] leading-6 text-muted-foreground">
                      {profileResult.profile.bio || emptyBioLabel}
                    </Text>

                    {/* Stats — 3-column with dividers */}
                    <View className="mb-5 flex-row border-t border-border pt-5">
                      <View className="flex-1 items-center gap-1">
                        <Text className="text-[20px] font-bold">
                          {profileResult.stats.hosted_count}
                        </Text>
                        <Text className="text-[10px] font-semibold uppercase tracking-[1.2px] text-muted-foreground">
                          Hosted
                        </Text>
                      </View>
                      <View className="my-1 w-px bg-border" />
                      <View className="flex-1 items-center gap-1">
                        <Text className="text-[20px] font-bold">
                          {profileResult.stats.follower_count}
                        </Text>
                        <Text className="text-[10px] font-semibold uppercase tracking-[1.2px] text-muted-foreground">
                          Followers
                        </Text>
                      </View>
                      <View className="my-1 w-px bg-border" />
                      <View className="flex-1 items-center gap-1">
                        <Text className="text-[20px] font-bold">
                          {profileResult.stats.following_count}
                        </Text>
                        <Text className="text-[10px] font-semibold uppercase tracking-[1.2px] text-muted-foreground">
                          Following
                        </Text>
                      </View>
                    </View>

                    {!isSelf && !isVenueProfile ? (
                      <View>
                        <Pressable
                          className="h-12 flex-row items-center justify-center gap-2 rounded-[18px] bg-[#ef6a3d]"
                          style={{ opacity: isFollowPending ? 0.55 : 1 }}
                          disabled={isFollowPending}
                          onPress={() => toggleFollow().catch(() => undefined)}>
                          <Icon as={UserPlus2Icon} size={16} className="text-[#140f0d]" />
                          <Text className="text-sm font-semibold text-primary-foreground">
                            {isFollowing ? 'Following' : followLabel}
                          </Text>
                        </Pressable>
                      </View>
                    ) : null}
                    {!isSelf && isVenueProfile && profileResult.profile.venue_id ? (
                      <View>
                        <Pressable
                          className="h-12 flex-row items-center justify-center rounded-[18px] bg-[#ef6a3d]"
                          onPress={openVenuePage}>
                          <Text className="text-sm font-semibold text-primary-foreground">
                            View Venue Page
                          </Text>
                        </Pressable>
                      </View>
                    ) : null}

                    {sceneTags.length > 0 ? (
                      <View className="mt-5 flex-row flex-wrap gap-2">
                        {sceneTags.map((tag) => (
                          <View
                            key={tag}
                            className="rounded-full border border-white/10 bg-white/5 px-3 py-2">
                            <Text className="text-[11px] font-semibold uppercase tracking-[1.4px] text-muted-foreground">
                              {getEventTagLabel(tag)}
                            </Text>
                          </View>
                        ))}
                      </View>
                    ) : null}
                  </SurfaceCard>
                </View>

                <View className="mt-4 px-4">
                  {shouldShowVenueShell ? (
                    <VenueProfileShell
                      venueName={
                        profileResult.profile.venue_name || profileResult.profile.display_name
                      }
                      avatarURL={profileAvatarURL.length > 0 ? profileAvatarURL : undefined}
                      username={profileResult.profile.username}
                      isStaff={profileResult.profile.is_staff}
                      onOpenVenuePage={openVenuePage}
                    />
                  ) : profileResult.visibility === 'limited' ? (
                    <View className="items-center gap-4 px-6 py-4">
                      <View
                        className="items-center justify-center rounded-full border border-white/10 bg-white/5"
                        style={{ width: 56, height: 56 }}>
                        <Icon as={LockIcon} size={22} className="text-muted-foreground" />
                      </View>
                      <View className="items-center gap-2">
                        <Text className="text-center text-[22px] font-extrabold tracking-tight">
                          Private profile.
                        </Text>
                        <Text className="max-w-[260px] text-center text-sm leading-6 text-muted-foreground">
                          Plans and hosted events stay hidden until you both follow each other.
                        </Text>
                      </View>
                    </View>
                  ) : (
                    <>
                      <SectionHeader title={activeSectionTitle} />
                      <View className="mt-4">
                        <ProfileSectionTabs
                          activeSection={activeProfileSection}
                          hostedCount={hostedEvents.length}
                          onChange={setActiveProfileSection}
                          pastCount={pastHostedEvents.length}
                          planCount={publicRSVPs.length}
                        />
                      </View>

                      {activeProfileSection === 'hosted' && hostedEvents.length ? (
                        <>
                          <View className="mb-3 mt-4">
                            <ProfileSectionMetaLine items={activeSectionMeta} />
                          </View>
                          <Text className="mb-4 mt-1 text-[14px] leading-5 text-muted-foreground">
                            {activeSectionBody}
                          </Text>
                          <FlatList
                            horizontal
                            data={hostedEvents}
                            keyExtractor={(e) => e.id}
                            showsHorizontalScrollIndicator={false}
                            className="mt-1"
                            contentContainerStyle={{ gap: 14, paddingRight: 8 }}
                            renderItem={renderHostedEventCard}
                          />
                        </>
                      ) : activeProfileSection === 'plans' && publicRSVPs.length ? (
                        <>
                          <View className="mb-3 mt-4">
                            <ProfileSectionMetaLine items={activeSectionMeta} />
                          </View>
                          <Text className="mb-4 mt-1 text-[14px] leading-5 text-muted-foreground">
                            {activeSectionBody}
                          </Text>
                          <FlatList
                            horizontal
                            data={publicRSVPs}
                            keyExtractor={(e) => `public-${e.id}`}
                            showsHorizontalScrollIndicator={false}
                            className="mt-1"
                            contentContainerStyle={{ gap: 14, paddingRight: 8 }}
                            renderItem={renderPublicPlanCard}
                          />
                        </>
                      ) : activeProfileSection === 'past' && pastHostedEvents.length ? (
                        <>
                          <View className="mb-3 mt-4">
                            <ProfileSectionMetaLine items={activeSectionMeta} />
                          </View>
                          <Text className="mb-4 mt-1 text-[14px] leading-5 text-muted-foreground">
                            {activeSectionBody}
                          </Text>
                          <FlatList
                            horizontal
                            data={pastHostedEvents}
                            keyExtractor={(e) => `past-${e.id}`}
                            showsHorizontalScrollIndicator={false}
                            className="mt-1"
                            contentContainerStyle={{ gap: 14, paddingRight: 8 }}
                            renderItem={renderPastHostedEventCard}
                          />
                        </>
                      ) : (
                        <View className="mt-4">
                          <EmptyState
                            title="Quiet for now."
                            body="There are no active hosted events or public plans on this profile right now."
                          />
                        </View>
                      )}
                    </>
                  )}
                </View>
              </View>
            )}
          </ScrollView>
        </View>
      </SafeAreaView>

      {Platform.OS !== 'web' ? (
        <ReportSheet
          ref={reportSheetRef}
          target={reportTarget}
          onReport={submitReport}
          onDismiss={handleReportDismiss}
        />
      ) : null}
    </>
  );
}
